import { PageHero } from '@components/common';
import { getServiceCard } from '@data/serviceCards';
import {
  Target,
  AlertTriangle,
  CheckCircle,
  ArrowRight,
  ShieldAlert,
  Search,
  ClipboardList,
  BarChart3,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { CALENDLY_URL } from '@constants';

const offerings = [
  {
    icon: Search,
    title: 'Comprehensive Risk Assessment',
    description:
      'Systematic evaluation of your assets, threats, vulnerabilities, and controls across people, process, and technology.',
  },
  {
    icon: Target,
    title: 'Threat Modelling',
    description:
      'Structured analysis of potential threats to your critical assets and systems, prioritised by likelihood and impact.',
  },
  {
    icon: ClipboardList,
    title: 'Risk Register Development',
    description:
      'Comprehensive risk register with clear ownership, treatment plans, and tracking mechanisms.',
  },
  {
    icon: BarChart3,
    title: 'Third-Party Risk Review',
    description:
      'Assessment of supply chain and vendor risks, including critical third-party security evaluations.',
  },
];

const whenYouNeedIt = [
  'You need to understand your current security posture',
  'Preparing for investment, acquisition, or sale',
  'Planning significant technology or organisational changes',
  'Need to satisfy investor or board risk requirements',
  'Experienced a security incident and need to understand gaps',
  <>
    Required for{' '}
    <Link to="/services/compliance" className="text-secondary hover:underline">
      compliance
    </Link>{' '}
    (ISO 27001, SOC 2, etc.)
  </>,
  'Annual risk review and refresh',
];

const methodology = [
  {
    step: '01',
    title: 'Scoping & Planning',
    description:
      'Define the scope, identify key stakeholders, and establish the assessment framework and methodology.',
  },
  {
    step: '02',
    title: 'Asset & Threat Identification',
    description:
      'Identify critical assets, data flows, and potential threats specific to your business context.',
  },
  {
    step: '03',
    title: 'Vulnerability Assessment',
    description:
      'Evaluate existing controls and identify vulnerabilities across technical and non-technical domains.',
  },
  {
    step: '04',
    title: 'Risk Analysis & Prioritisation',
    description:
      'Analyse risks using qualitative and quantitative methods to prioritise based on business impact.',
  },
  {
    step: '05',
    title: 'Treatment Planning',
    description:
      'Develop actionable mitigation strategies with cost-benefit analysis and implementation timelines.',
  },
  {
    step: '06',
    title: 'Reporting & Recommendations',
    description:
      'Deliver comprehensive findings with executive summary, detailed analysis, and roadmap for improvement.',
  },
];

export default function RiskAssessmentPage() {
  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Risk Assessment Services',
    description:
      'Gain clear visibility into your cyber risks with comprehensive assessments that prioritise what matters most',
    url: 'https://thecisonetwork.com/services/risk-assessment',
    provider: {
      '@type': 'Organization',
      name: 'The CISO Network',
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }}
      />
      <PageHero
        title="Risk Assessment Services"
        subtitle="Gain clear visibility into your cyber risks with comprehensive assessments that prioritise what matters most"
        eyebrow="Our Services"
        backgroundImage={getServiceCard('risk-assessment')?.image}
        backgroundAlt={getServiceCard('risk-assessment')?.imageAlt}
        seoTitle="Cybersecurity Risk Assessment | The CISO Network"
        seoDescription="Comprehensive cyber risk assessments from experienced fractional CISOs. Identify vulnerabilities, assess threats, and prioritise mitigation strategies."
      />

      {/* Overview */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-6">
                You Cannot Protect What You Do Not Understand
              </h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  Many organisations struggle with unclear security postures. They know they face
                  threats but lack visibility into which risks matter most or where to focus limited
                  resources. Without a clear understanding of your risk landscape, security spending
                  becomes reactive and inefficient.
                </p>
                <p>
                  Our risk assessments give you a clear, prioritised view of your cyber risks. We go
                  beyond automated scanning tools to understand your business context, critical
                  assets, and specific threat landscape. The result is actionable intelligence that
                  guides{' '}
                  <Link to="/services/security-strategy" className="text-secondary hover:underline">
                    strategic security decisions
                  </Link>
                  .
                </p>
                <p>
                  Whether you need a comprehensive enterprise risk assessment or focused evaluation
                  of specific systems, our fractional CISOs bring the expertise to deliver insights
                  that drive action.
                </p>
              </div>
            </div>
            <div className="bg-background rounded-2xl p-8 border border-border">
              <div className="flex items-center gap-4 mb-6 justify-center">
                <div className="h-12 w-12 rounded-xl bg-teal flex items-center justify-center">
                  <ShieldAlert className="h-6 w-6 text-accent-foreground" />
                </div>
                <h3 className="text-xl font-heading font-bold text-foreground">
                  When You Need This
                </h3>
              </div>
              <div className="flex justify-center">
                <ul className="space-y-4 inline-block text-left">
                  {whenYouNeedIt.map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-muted-foreground">
                      <CheckCircle className="h-5 w-5 text-teal flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Offerings */}
      <section className="py-24 bg-background">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              Assessment Capabilities
            </h2>
            <p className="text-muted-foreground max-w-3xl mx-auto">
              Comprehensive risk evaluation covering all dimensions of your security posture
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {offerings.map((offering, i) => (
              <div key={i} className="bg-white rounded-2xl p-8 border border-border">
                <div className="h-12 w-12 rounded-xl bg-teal/10 flex items-center justify-center mb-6">
                  <offering.icon className="h-6 w-6 text-teal" />
                </div>
                <h3 className="text-xl font-heading font-bold text-foreground mb-3">
                  {offering.title}
                </h3>
                <p className="text-muted-foreground">{offering.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Risk Matrix Visual */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-6">
                Risk-Based Prioritisation
              </h2>
              <p className="text-muted-foreground mb-6">
                Not all risks are equal. Our assessments use proven frameworks to evaluate risks
                based on their potential business impact and likelihood of occurrence. This ensures
                you focus resources on what matters most.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="h-10 w-10 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0">
                    <AlertTriangle className="h-5 w-5 text-red-600" />
                  </div>
                  <div>
                    <h4 className="font-heading font-semibold text-foreground">Critical Risks</h4>
                    <p className="text-muted-foreground text-sm">
                      High impact, high likelihood - require immediate attention and mitigation
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="h-10 w-10 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                    <AlertTriangle className="h-5 w-5 text-amber-600" />
                  </div>
                  <div>
                    <h4 className="font-heading font-semibold text-foreground">High Risks</h4>
                    <p className="text-muted-foreground text-sm">
                      Significant impact or likelihood - prioritise for near-term action
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="h-10 w-10 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-heading font-semibold text-foreground">Acceptable Risks</h4>
                    <p className="text-muted-foreground text-sm">
                      Lower impact and likelihood - monitor and manage through normal operations
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-background rounded-2xl p-8 border border-border">
              <h3 className="text-xl font-heading font-bold text-foreground mb-6 text-center">
                What You Will Receive
              </h3>
              <div className="flex justify-center">
                <ul className="space-y-4 inline-block text-left">
                  {[
                    'Executive risk summary suitable for board presentation',
                    'Detailed risk register with risk scores and priorities',
                    'Identified vulnerabilities and control gaps',
                    'Threat analysis specific to your industry and context',
                    'Prioritised treatment recommendations with cost estimates',
                    'Implementation roadmap for risk mitigation',
                    'Risk monitoring and reporting framework',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-muted-foreground">
                      <CheckCircle className="h-5 w-5 text-teal flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Methodology */}
      <section className="py-24 bg-background">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              Our Assessment Methodology
            </h2>
            <p className="text-muted-foreground max-w-3xl mx-auto">
              A structured approach based on industry standards including NIST, ISO 27005, and FAIR
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {methodology.map((step, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 border border-border">
                <div className="text-4xl font-heading font-bold text-teal/20 mb-4">{step.step}</div>
                <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
                  {step.title}
                </h3>
                <p className="text-muted-foreground text-sm">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 gradient-navy cta-photo">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary-foreground mb-6">
            Understand Your Risk Landscape
          </h2>
          <p className="text-primary-foreground/60 text-lg max-w-3xl mx-auto mb-10">
            Book a discovery call to discuss your risk assessment needs and how our{' '}
            <Link to="/what-is-a-fractional-ciso" className="text-teal hover:underline">
              fractional CISOs
            </Link>{' '}
            can provide the visibility you need.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="btn-hero">
              Book a Discovery Call <ArrowRight className="ml-2 h-4 w-4" />
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
